import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
    ChangeValue,
    PropertyConfigCode,
    SmartNumber,
    SmartNumberDescription,
    SmartNumberMapping,
} from "@/features/sub-families/types";
import type { Nullable, SmartNumberDescriptorKey } from "@/types";

type State = {
    mapping: Nullable<SmartNumberMapping>;
    description: SmartNumberDescription;
    calculatedResult: Nullable<string>;
    usesDifferentSmartNumber: boolean;
    /** @desc Used to fetch configuration and glb file */
    subFamily: Nullable<SmartNumber>;
};

type ChangeAllColoursPayload = { descriptors: SmartNumberDescriptorKey[]; value: PropertyConfigCode };

const initialState: State = {
    mapping: null,
    description: [],
    calculatedResult: null,
    usesDifferentSmartNumber: false,
    subFamily: null,
};

const slice = createSlice({
    name: "smart-number",
    initialState,
    reducers: {
        smartNumberSetSubFamilyAction: (state: State, action: PayloadAction<Nullable<SmartNumber>>) => {
            state.subFamily = action.payload;
        },
        smartNumberSetDifferentAction: (state: State, action: PayloadAction<boolean>) => {
            state.usesDifferentSmartNumber = action.payload;
        },
        smartNumberSetDescriptionAction: (state: State, action: PayloadAction<SmartNumberDescription>) => {
            state.description = action.payload;
        },
        smartNumberSetMappingAction: (state: State, action: PayloadAction<SmartNumberMapping>) => {
            state.mapping = action.payload;
        },
        /** @description Does not trigger any middleware - just a plain override */
        smartNumberOverrideMappingAction: (state: State, action: PayloadAction<SmartNumberMapping>) => {
            state.mapping = action.payload;
        },
        smartNumberSetCalculatedResultAction: (state: State, action: PayloadAction<string>) => {
            state.calculatedResult = action.payload;
        },
        smartNumberChangeMappingAction: (state: State, action: PayloadAction<ChangeValue>) => {
            if (!state.mapping) return; // Should only be done when a mapping is already defined

            state.mapping[action.payload.descriptor] = action.payload.configCode;
        },
        smartNumberChangeMultipleMappingAction: (state: State, action: PayloadAction<ChangeValue[]>) => {
            if (!state.mapping) return; // Should only be done when a mapping is already defined

            for (const definition of action.payload) {
                state.mapping[definition.descriptor] = definition.configCode;
            }
        },
        smartNumberChangeMultipleColoursAction: (state: State, action: PayloadAction<ChangeAllColoursPayload>) => {
            if (!state.mapping) return; // Should only be done when a mapping is already defined

            const { descriptors, value } = action.payload;

            for (const descriptor of descriptors) {
                state.mapping[descriptor] = value;
            }
        },
    },
});

export const {
    smartNumberSetSubFamilyAction,
    smartNumberSetDifferentAction,
    smartNumberSetDescriptionAction,
    smartNumberSetMappingAction,
    smartNumberOverrideMappingAction,
    smartNumberSetCalculatedResultAction,
    smartNumberChangeMappingAction,
    smartNumberChangeMultipleMappingAction,
    smartNumberChangeMultipleColoursAction,
} = slice.actions;

export default slice.reducer;
